import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import type { Routes } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { IconSize } from '@freelancer/ui/icon';
import type { Tab } from '@freelancer/ui/tabs';
import { TabsDirection, TabsSize } from '@freelancer/ui/tabs';
import { isDefined } from '@freelancer/utils';
import { appRoutes } from 'bits/app-routing.module';
import { HomeComponent } from 'bits/home/home.component';
import { componentStoryMap } from '../../generated/component-story-map';
import { patternStoryMap } from '../../generated/patterns-story-map';
import { prettyPrint } from '../../helpers';

interface SidebarCategoryMap {
  [k: string]: readonly string[];
}

interface SidebarStoryMap {
  [name: string]: {
    path: string;
    internalOnly: boolean;
    tags: readonly string[];
    stories: readonly string[];
  };
}

interface SidebarItem {
  title: string;
  tabs: readonly Tab[];
}

@Component({
  standalone: false,
  selector: 'app-sidebar',
  template: `
    <div class="SidebarHeader mb-small">
      <fl-icon
        class="SidebarHeader-action mobile:show"
        [name]="'ui-sidebar-left'"
        [size]="IconSize.SMALL"
        [clickable]="true"
        (click)="handleCloseSidebar()"
      ></fl-icon>
    </div>
    <fl-scrollable-content class="SidebarContent">
      @for (item of sidebarItems; track trackByTabTitle($index, item)) {
        @if (hasCategories) {
          <div
            class="Header mb-xxsmall mr-xxsmall"
            (click)="toggleSection(item.title, content)"
          >
            <h3 class="text-body-16 CategoryTitle font-medium">
              {{ item.title }}
            </h3>
            <fl-icon
              class="Chevron"
              [class.Expanded]="expandedSections[item.title]"
              [name]="'ui-chevron-right'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          </div>
          <div
            #content
            class="Content"
            [class.Expanded]="expandedSections[item.title]"
            [class.Collapsed]="!expandedSections[item.title]"
          >
            @if (hasCategories) {
              <ng-container [ngTemplateOutlet]="tabs"></ng-container>
            }
          </div>
        } @else {
          <ng-container [ngTemplateOutlet]="tabs"></ng-container>
        }
        <ng-template #tabs>
          <fl-tabs
            [direction]="TabsDirection.COLUMN_RIGHT"
            [size]="TabsSize.XSMALL"
          >
            @for (tab of item.tabs; track trackByTabTitle($index, tab)) {
              <fl-tab-item
                [titleText]="tab.title"
                [link]="tab.link"
                [routeHasChildren]="true"
                [shouldPrefetchRoute]="true"
              ></fl-tab-item>
            }
          </fl-tabs>
        </ng-template>
      }
    </fl-scrollable-content>
  `,
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);

  IconSize = IconSize;
  TabsDirection = TabsDirection;
  TabsSize = TabsSize;

  sidebarItems: readonly SidebarItem[];
  hasCategories = false;
  expandedSections: { [key: string]: boolean } = {};
  sectionHeights: { [key: string]: number } = {}; // Store the height of each section

  @Output() closeSidebar = new EventEmitter<void>();
  @Input() hideInternalPages = true;

  ngOnInit(): void {
    this.generateSidebarItems();
    this.initializeExpandedSections();
    this.checkIfCategoriesPage();
  }

  private initializeExpandedSections(): void {
    this.expandedSections = Object.fromEntries(
      this.sidebarItems.map(item => [item.title, false]),
    );
  }

  toggleSection(title: string, content: HTMLElement): void {
    // Toggle the section's expanded/collapsed state
    this.expandedSections[title] = !this.expandedSections[title];

    // Set the max-height dynamically
    if (this.expandedSections[title]) {
      this.sectionHeights[title] = content.scrollHeight + 8;
    } else {
      // Collapse the section
      this.sectionHeights[title] = 0;
    }
  }

  generateSidebarItems(): void {
    const { routeConfig } = this.activatedRoute.snapshot;

    if (!routeConfig) {
      return;
    }

    const { path } = routeConfig;
    let { children } = routeConfig;

    if (!isDefined(path)) {
      return;
    }

    if (routeConfig.component === HomeComponent) {
      children = appRoutes[1].children;
    }

    if (path === 'components') {
      this.sidebarItems = this.getCategorizedTabs(path, componentStoryMap);
      return;
    }

    if (path === 'patterns') {
      this.sidebarItems = this.getCategorizedTabs(path, patternStoryMap);
      return;
    }

    if (isDefined(children)) {
      this.sidebarItems = this.getUnCategorizedTabs(path, children);
    }
  }

  private checkIfCategoriesPage(): void {
    const pathSegments = this.activatedRoute.snapshot.url.map(
      segment => segment.path,
    );

    this.hasCategories =
      pathSegments.includes('components') || pathSegments.includes('patterns');
  }

  /**
   * Categorized stories based on their tag
   * defined in their respective readme.md
   */
  private getCategorizedTabs(
    route: string,
    storyMap: SidebarStoryMap,
  ): readonly SidebarItem[] {
    const groupedStories = Object.keys(storyMap).reduce(
      (all: SidebarCategoryMap, category) => {
        if (!storyMap[category]) {
          return all;
        }

        // use a first tag as its category
        const tag = storyMap[category].tags[0];

        return {
          ...all,
          [tag]: all[tag] ? [...all[tag], category] : [category],
        };
      },
      {},
    );

    return Object.entries(groupedStories)
      .sort()
      .map(([category, children]) => ({
        title: prettyPrint(category || ''),
        tabs: children
          .filter(item => {
            if (!this.hideInternalPages) {
              return true;
            }

            return !storyMap[item].internalOnly;
          })
          .map(item => ({
            link: `/${route}/${item}`,
            title: prettyPrint(item || ''),
          })),
      }))
      .filter(story => story.tabs.length > 0);
  }

  /**
   * Uncategorized version of the tabs
   */
  private getUnCategorizedTabs(
    path: string,
    items: Routes,
  ): readonly SidebarItem[] {
    return [
      {
        title: '',
        tabs: items
          .filter(item => isDefined(item.path) && item.path !== '')
          .filter(item => {
            if (!this.hideInternalPages || !isDefined(item.data)) {
              return true;
            }

            return !item.data.internalOnly;
          })
          .map(item => ({
            link: `/${path}/${item.path}`,
            title: prettyPrint(item.path || ''),
          })),
      },
    ];
  }

  handleCloseSidebar(): void {
    this.closeSidebar.emit();
  }

  trackByTabTitle(_: number, tab: Tab | SidebarItem): string {
    return tab.title;
  }
}
